import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AlterMenuSC } from "./AlterMenu.sc";

const HEADER_BAND_STYLE = {
  whiteSpace: "nowrap",
  padding: "0.1rem 0 0.2rem",
  fontSize: "0.7em",
  fontWeight: 500,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  opacity: 0.7,
};

const PROVIDER_NAME_OVERRIDES = {
  Microsoft: "Azure",
};

function shortenSource(alt) {
  // Source comes in as "Provider - variant" (e.g. "Microsoft - with context",
  // "Google - MWE phrase"); collapse to just the provider name.
  const provider = (alt.source || "").split(" - ")[0];
  return PROVIDER_NAME_OVERRIDES[provider] || provider || alt.source;
}

// ADR 022: `word.alternatives` carries the full vote-ordered provider list
// (winner at index 0) directly from /translate_word. The menu shows the non-winner
// entries — dedupe by normalised translation text and drop anything equal
// to the current primary translation.
//
// `allAgreedWithPrimary` is true when the providers ran (alternatives is
// non-empty in some source) but everything they returned matched the
// primary. The header turns into an affirmation instead of "no alternatives
// found", so the user can tell the system did look — it just confirmed
// the translation they already had. (Especially common for past-translation
// words: the user previously accepted the providers' consensus, so when we
// ask the providers again they say the same thing.)
//
// `word.competing_translations` is the legacy field kept during ADR 022's
// deprecation window; consulted for clients (extension build) that still
// only receive it. Safe to drop once the extension reads `alternatives`.
function buildAlternatives(word) {
  const seen = new Set();
  const list = [];
  let hadAnyEntry = false;
  const normaliseKey = (t) => (t || "").toLowerCase().trim();
  const primaryKey = normaliseKey(word.translation);
  if (primaryKey) seen.add(primaryKey);

  const sources = [word.competing_translations, word.alternatives];
  for (const src of sources) {
    if (!src) continue;
    for (const entry of src) {
      const key = normaliseKey(entry?.translation);
      if (!key) continue;
      hadAnyEntry = true;
      if (seen.has(key)) continue;
      seen.add(key);
      list.push(entry);
    }
  }
  return { list, allAgreedWithPrimary: hadAnyEntry && list.length === 0 };
}

export default function AlterMenu({
  word,
  hideAlterMenu,
  selectAlternative,
  editTranslation,
  deleteTranslation,
  ungroupMwe,
  explainSelection,
  loadingAlternatives = false,
}) {
  const refToAlterMenu = useRef(null);

  // Close the menu as soon as the user starts scrolling — the menu is
  // viewport-fixed, so without this it'd stay floating while the trigger
  // word moves away under it. touchmove catches the finger drag before
  // iOS gets around to firing scroll, so the menu disappears at gesture
  // start instead of after the page has already moved.
  //
  // This is also why the menu must not host a text input: focusing one
  // opens the soft keyboard, the browser scrolls to reveal the field, and
  // this handler tears the menu down mid-typing. Editing the translation
  // opens a real modal instead (see `editTranslation`).
  useEffect(() => {
    function handleScrollIntent(e) {
      const el = refToAlterMenu.current;
      if (e?.target && el && el.contains(e.target)) return;
      hideAlterMenu();
    }
    window.addEventListener("scroll", handleScrollIntent, { passive: true });
    window.addEventListener("touchmove", handleScrollIntent, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScrollIntent);
      window.removeEventListener("touchmove", handleScrollIntent);
    };
  }, [hideAlterMenu]);

  // Modal-style outside-click handling: a capture-phase document listener
  // fires before any target's own click handler, so we can stopPropagation
  // BEFORE the tapped word's clickOnWord runs. This way a tap outside the
  // menu just closes the menu — it doesn't translate the new word.
  useEffect(() => {
    function handleCapture(e) {
      const el = refToAlterMenu.current;
      if (el && !el.contains(e.target)) {
        // stopPropagation kills React's delegated onClick before it dispatches
        // (so the tapped word doesn't translate). preventDefault on touchend
        // also stops the browser from synthesizing the click that would
        // otherwise slip through after our menu unmounts.
        e.stopPropagation();
        e.preventDefault();
        hideAlterMenu();
      }
    }
    const opts = { capture: true, passive: false };
    document.addEventListener("click", handleCapture, opts);
    document.addEventListener("touchend", handleCapture, opts);
    return () => {
      document.removeEventListener("click", handleCapture, opts);
      document.removeEventListener("touchend", handleCapture, opts);
    };
  }, [hideAlterMenu]);

  const { list: filteredAlternatives, allAgreedWithPrimary } = buildAlternatives(word);
  const hasAlternatives = filteredAlternatives.length > 0;
  // Opened before the alternatives were there: they will be inserted above
  // the actions, so animate them in rather than shoving the actions down.
  const [alternativesArriveLate] = useState(loadingAlternatives);

  function placeMenu() {
    const el = refToAlterMenu.current;
    const trigger = el?.parentElement;
    if (!el || !trigger) return;
    const margin = 8;
    el.style.position = "fixed";
    el.style.margin = "0";

    // `position: fixed` is resolved against the nearest ancestor that
    // establishes a containing block — which is a transformed/filtered
    // ancestor, NOT always the viewport. The preview overlay's modal wrapper
    // centers itself with `transform: translate(-50%,-50%)`, so it becomes the
    // containing block for this fixed menu and our viewport-space math lands
    // the menu offset by the modal's top-left (far to the right on desktop,
    // where the modal is centered). Park the menu at (0,0) first and read back
    // where that corner sits in viewport coordinates; that delta lets us
    // translate viewport targets into containing-block-relative offsets. When
    // fixed really is viewport-relative (the reader, the mobile bottom sheet
    // with `transform: none`) the origin is (0,0) and this is a no-op.
    el.style.left = "0px";
    el.style.top = "0px";
    const origin = el.getBoundingClientRect();

    const triggerRect = trigger.getBoundingClientRect();
    const elWidth = el.offsetWidth;
    const elHeight = el.offsetHeight;
    let viewportLeft = triggerRect.left;
    if (viewportLeft + elWidth > window.innerWidth - margin) {
      viewportLeft = window.innerWidth - elWidth - margin;
    }
    if (viewportLeft < margin) viewportLeft = margin;

    const viewportTop =
      triggerRect.bottom + 4 + elHeight > window.innerHeight - margin
        ? Math.max(margin, triggerRect.top - elHeight - 4)
        : triggerRect.bottom + 4;

    el.style.left = `${viewportLeft - origin.left}px`;
    el.style.top = `${viewportTop - origin.top}px`;
  }

  // Reposition only when something that can change menu dimensions changes;
  // otherwise re-runs on every keystroke in the input force a reflow.
  useLayoutEffect(placeMenu, [hasAlternatives, filteredAlternatives.length, loadingAlternatives]);

  let headerLabel;
  if (loadingAlternatives) {
    headerLabel = "Checking translators…";
  } else if (word.disagreement) {
    headerLabel = (
      <>
        <span style={{ fontSize: "1.4em", verticalAlign: "middle", lineHeight: 1, borderBottom: "none" }}>🤖🥊</span>{" "}
        Translators disagree
      </>
    );
  } else if (hasAlternatives) {
    headerLabel = "Alternatives";
  } else if (allAgreedWithPrimary) {
    headerLabel = "All translators agree";
  } else {
    headerLabel = "No alternatives";
  }
  const header = <div style={{ ...HEADER_BAND_STYLE, color: "var(--altermenu-header-text)" }}>{headerLabel}</div>;

  return (
    <AlterMenuSC ref={refToAlterMenu}>
      {header}
      {hasAlternatives && (
        <div
          className={alternativesArriveLate ? "alternativesList growIn" : "alternativesList"}
          onAnimationEnd={placeMenu}
        >
          <div className="alternativesRows">
            {filteredAlternatives.map((each, index) => (
              <div
                key={`${each.translation}-${each.source}-${index}`}
                onClick={(e) => selectAlternative(each.translation, shortenSource(each))}
                className="additionalTrans"
              >
                {each.translation}
                <div className="altermenuSourceLabel">{shortenSource(each)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="actionsSection">
        {explainSelection && (
          <div className="neutralLink" onClick={() => explainSelection(word)}>
            Explain
          </div>
        )}
        {editTranslation && (
          <div className="neutralLink" onClick={() => editTranslation()}>
            Edit
          </div>
        )}
        {word.mweExpression && ungroupMwe && (
          <div className="neutralLink" onClick={(e) => ungroupMwe(e, word)}>
            Ungroup expression
          </div>
        )}
        <div className="removeLink" onClick={(e) => deleteTranslation(e, word)}>
          Remove
        </div>
      </div>
    </AlterMenuSC>
  );
}
